import { ReactNode, useCallback } from 'react'

import { clsx } from 'clsx'

import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { analyticsNumberFormatter } from '../utils/parser.js'

export enum ValueType {
  Percentage = 'percentage',
  Number = 'number',
}

interface StatCardProps {
  title: ReactNode
  description?: ReactNode
  value: number | string
  valueType?: ValueType
  icon?: ReactNode
  tooltip?: string
  isLoading?: boolean
  className?: string
}

export const StatCard = ({
  title,
  description,
  value,
  valueType = ValueType.Number,
  icon,
  tooltip,
  isLoading = false,
  className,
}: StatCardProps) => {
  const formatValue = useCallback(() => {
    if (isLoading) {
      return '-'
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    if (valueType === 'percentage') {
      return `${analyticsNumberFormatter(numValue)}%`
    }
    return analyticsNumberFormatter(numValue)
  }, [value, valueType, isLoading])

  return (
    <Card
      className={clsx(
        'p-4',
        className,
        isLoading && 'animate-pulse bg-surface-pressed',
      )}
    >
      <div className="flex items-start justify-between h-full">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h3 className="text-content text-lg font-medium">{title}</h3>
            {tooltip && (
              <Tooltip>
                <Tooltip.Trigger>
                  <SvgIcon
                    className="w-4 h-4 cursor-pointer text-content-subdued"
                    name="info"
                  />
                </Tooltip.Trigger>
                <Tooltip.Panel>{tooltip}</Tooltip.Panel>
              </Tooltip>
            )}
          </div>
          {description && (
            <p className="text-content-subdued text-sm">{description}</p>
          )}
          <div className="mt-2 text-heading-sm text-content">
            {formatValue()}
          </div>
        </div>
        {icon && (
          <div className="flex shrink-0 justify-center items-center rounded-full w-10 h-10 bg-surface-hovered text-content-subdued">
            {icon}
          </div>
        )}
      </div>
    </Card>
  )
}
